import { todayStr } from "../../lib/date";
import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { getSchoolClosures, addSchoolClosure, deleteSchoolClosure, getDrills, addDrill, deleteDrill } from "../../lib/api";

const CLOSURE_TYPES = [
  { value: "snow_day", label: "Snow Day" },
  { value: "no_heat", label: "No Heat" },
  { value: "flood", label: "Flooding" },
  { value: "power_outage", label: "Power Outage" },
  { value: "emergency", label: "Emergency" },
  { value: "other", label: "Other" },
];

const DRILL_TYPES = ["Fire Drill", "Evacuation", "Shelter in Place", "Lockdown", "Lockout", "Reverse Evacuation", "Other"];

export function SchoolClosuresSection({ academicYear }) {
  const [closures, setClosures] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: todayStr(), type: "snow_day", reason: "" });

  useEffect(() => {
    getSchoolClosures().then(res => setClosures(res.data || [])).catch(() => {});
  }, []);

  // When an academic year (e.g. "2025-2026") is supplied, only show closures that
  // fall within that school year's span (Jul of the start year → Jun of the end year).
  const yearRange = useMemo(() => {
    if (!academicYear || !/^\d{4}-\d{4}$/.test(academicYear)) return null;
    const [start, end] = academicYear.split("-");
    return { start: `${start}-07-01`, end: `${end}-06-30` };
  }, [academicYear]);

  const visibleClosures = yearRange
    ? closures.filter(c => c.date >= yearRange.start && c.date <= yearRange.end)
    : closures;

  const handleAdd = async () => {
    if (!form.date) { toast.error("Date required"); return; }
    try {
      const res = await addSchoolClosure(form);
      setClosures(prev => [res.data, ...prev]);
      setShowForm(false);
      setForm({ date: todayStr(), type: "snow_day", reason: "" });
      toast.success("Closure recorded");
    } catch { toast.error("Failed to save"); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteSchoolClosure(id);
      setClosures(prev => prev.filter(c => c.id !== id));
      toast.success("Closure removed");
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <Card data-testid="school-closures-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Unplanned Closures</CardTitle>
            <CardDescription>Snow days, emergencies, etc. These are excluded from the school day count.</CardDescription>
          </div>
          <Button size="sm" onClick={() => setShowForm(!showForm)} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="add-closure-btn">
            {showForm ? "Cancel" : "+ Add Closure"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {showForm && (
          <div className="flex flex-wrap items-end gap-3 p-3 rounded-lg border bg-muted/30 mb-4">
            <div className="space-y-1">
              <Label className="text-xs">Date</Label>
              <Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="w-40 h-8" data-testid="closure-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Type</Label>
              <Select value={form.type} onValueChange={v => setForm(p => ({ ...p, type: v }))}>
                <SelectTrigger className="w-40 h-8" data-testid="closure-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CLOSURE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 flex-1">
              <Label className="text-xs">Reason / Notes</Label>
              <Input value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))} placeholder="e.g. Heavy snowfall" className="h-8" data-testid="closure-reason" />
            </div>
            <Button size="sm" onClick={handleAdd} data-testid="save-closure-btn">Save</Button>
          </div>
        )}
        {visibleClosures.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No unplanned closures recorded this year.</p>
        ) : (
          <div className="space-y-2">
            {visibleClosures.map(c => (
              <div key={c.id} className="flex items-center justify-between p-2 rounded-lg bg-orange-50 border border-orange-200" data-testid={`closure-${c.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium">{c.date} — {CLOSURE_TYPES.find(t => t.value === c.type)?.label || c.type}</p>
                    {c.reason && <p className="text-xs text-muted-foreground">{c.reason}</p>}
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="text-red-500 h-7" onClick={() => handleDelete(c.id)}>Remove</Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function DrillLogSection() {
  const [drills, setDrills] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: todayStr(), time: "", type: "Fire Drill", notes: "", head_count: "", duration_minutes: "" });

  useEffect(() => {
    getDrills().then(res => setDrills(res.data || [])).catch(() => {});
  }, []);

  const handleAdd = async () => {
    if (!form.date || !form.type) { toast.error("Date and type required"); return; }
    const data = { ...form };
    if (data.head_count) data.head_count = parseInt(data.head_count) || null;
    if (data.duration_minutes) data.duration_minutes = parseInt(data.duration_minutes) || null;
    try {
      const res = await addDrill(data);
      setDrills(prev => [res.data, ...prev]);
      setShowForm(false);
      setForm({ date: todayStr(), time: "", type: "Fire Drill", notes: "", head_count: "", duration_minutes: "" });
      toast.success("Drill logged");
    } catch { toast.error("Failed to save"); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDrill(id);
      setDrills(prev => prev.filter(d => d.id !== id));
      toast.success("Drill removed");
    } catch { toast.error("Failed to delete"); }
  };

  const getDrillColor = (type) => {
    const colors = {
      "Fire Drill": { bg: "bg-red-50", border: "border-red-200", icon: "text-red-600" },
      "Evacuation": { bg: "bg-amber-50", border: "border-amber-200", icon: "text-amber-600" },
      "Shelter in Place": { bg: "bg-purple-50", border: "border-purple-200", icon: "text-purple-600" },
      "Lockdown": { bg: "bg-slate-100", border: "border-slate-300", icon: "text-slate-700" },
      "Lockout": { bg: "bg-gray-50", border: "border-gray-300", icon: "text-gray-600" },
    };
    return colors[type] || { bg: "bg-blue-50", border: "border-blue-200", icon: "text-blue-600" };
  };

  return (
    <Card data-testid="drill-log-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Safety Drill Log</CardTitle>
            <CardDescription>Monthly fire, evacuation, shelter-in-place, and other safety drills.</CardDescription>
          </div>
          <Button size="sm" onClick={() => setShowForm(!showForm)} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="add-drill-btn">
            {showForm ? "Cancel" : "+ Log Drill"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {showForm && (
          <div className="flex flex-wrap items-end gap-3 p-3 rounded-lg border bg-muted/30 mb-4">
            <div className="space-y-1">
              <Label className="text-xs">Date</Label>
              <Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="w-40 h-8" data-testid="drill-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Time</Label>
              <Input type="time" value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))} className="w-32 h-8" data-testid="drill-time" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Drill Type</Label>
              <Select value={form.type} onValueChange={v => setForm(p => ({ ...p, type: v }))}>
                <SelectTrigger className="w-44 h-8" data-testid="drill-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DRILL_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Head Count</Label>
              <Input type="number" value={form.head_count} onChange={e => setForm(p => ({ ...p, head_count: e.target.value }))} placeholder="e.g. 12" className="w-24 h-8" data-testid="drill-headcount" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Duration (min)</Label>
              <Input type="number" value={form.duration_minutes} onChange={e => setForm(p => ({ ...p, duration_minutes: e.target.value }))} placeholder="e.g. 5" className="w-24 h-8" data-testid="drill-duration" />
            </div>
            <div className="space-y-1 flex-1">
              <Label className="text-xs">Notes</Label>
              <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Any observations..." className="h-8" data-testid="drill-notes" />
            </div>
            <Button size="sm" onClick={handleAdd} data-testid="save-drill-btn">Save</Button>
          </div>
        )}
        {drills.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No drills logged yet this year.</p>
        ) : (
          <div className="space-y-2">
            {drills.map(d => {
              const color = getDrillColor(d.type);
              return (
                <div key={d.id} className={`flex items-center justify-between p-2 rounded-lg ${color.bg} border ${color.border}`} data-testid={`drill-${d.id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full bg-white flex items-center justify-center ${color.icon}`}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="m9 12 2 2 4-4"/></svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium">{d.date}{d.time ? ` at ${d.time}` : ""} — {d.type}</p>
                      <div className="flex gap-3 text-xs text-muted-foreground">
                        {d.head_count && <span>Head count: {d.head_count}</span>}
                        {d.duration_minutes && <span>Duration: {d.duration_minutes} min</span>}
                        {d.notes && <span>{d.notes}</span>}
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-500 h-7" onClick={() => handleDelete(d.id)}>Remove</Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
